import React from 'react';
import { useFeatureFlags } from 'providers/FeatureFlagsProvider';
import { EntityRoutes } from 'routes/constants';
import strings from 'strings';
import { InfoLabelModes } from 'UI/components/molecules/InfoLabel';
import { KeywordTags } from 'UI/components/molecules/KeywordTags';
import { ProfileAlert } from 'UI/components/molecules/ProfileAlert';
import InventoryOverviewLayout from 'UI/components/organisms/inventoryProfiles//InventoryOverviewLayout';
import InfoLabelsGrid from 'UI/components/organisms/inventoryProfiles/InfoLabelsGrid';
import { EntityType } from 'UI/constants/entityTypes';
import { FeatureFlags } from 'UI/constants/featureFlags';
import { nestTernary, phoneNumberFormatter } from 'UI/utils';

import {
  /**
   * @typedef {Object} OverviewActionsProps
   * @property {Function} onNotesClick
   * @property {Function} onActivitiesClick
   */
  /**
   * @typedef {Object} OverviewSectionsProps
   * @property {Object[]} activityLogs
   * @property {Object[]} notes
   */
  buildEmailLabels,
  buildKeywordTagsCard,
  extractFullName,
  getEmailValidationAlerts,
  getPhonesLabelsForProfile
} from '../utils';

const {
  shared: {
    fields: { currentCompany: currentCompanyString }
  }
} = strings;

/**
 * @param {Object} props
 * @param {Object} [props.profile]
 * @param {boolean} props.loading
 * @param {OverviewSectionsProps} props.overviewSections
 * @param {OverviewActionsProps} props.overviewActions
 */
const HiringAuthorityOverview = ({ loading, profile }) => {
  const hasMultiplePhones = !!profile.phone_id;
  const { checkIfFeatureFlagEnabled } = useFeatureFlags();
  const shouldShowKeywordTags = checkIfFeatureFlagEnabled(FeatureFlags.ContactKeywordTags);
  const isMultiplePhonesEnabled = checkIfFeatureFlagEnabled(
    FeatureFlags.HiringAuthoritiesMultiplePhones
  );

  const fullName = extractFullName(profile, EntityType.HiringAuthority);

  const legacyPhoneLabels = [
    {
      title: 'Phone',
      mode: 'ringCentral',
      linkProps: {
        url: phoneNumberFormatter(profile?.work_phone),
        id: profile?.id,
        role: EntityType.HiringAuthority,
        customLinkProps: {
          profileName: fullName
        }
      }
    },
    {
      title: 'EXT',
      description: profile?.ext
    },

    {
      title: 'Other Phone ',
      mode: 'ringCentral',
      linkProps: {
        url: phoneNumberFormatter(profile?.personal_phone),
        id: profile?.id,
        role: EntityType.HiringAuthority,
        customLinkProps: {
          profileName: fullName
        }
      }
    },
    {
      title: 'Other EXT',
      description: profile?.other_ext
    }
  ];

  const generalLabels = [
    {
      title: 'Nickname',
      description: profile?.nickname
    },
    {
      title: 'Title',
      description: profile?.title
    },
    {
      title: strings.shared.fields.jobFunction,
      description: profile?.position?.title
    },
    { title: 'Industry: Specialty', description: profile?.specialty?.title },
    { title: 'Subspecialty', description: profile?.subspecialty?.title },
    {
      title: currentCompanyString,
      mode: profile?.company_id ? 'localLink' : 'text',
      description: profile?.company?.name,
      itemId: profile?.company_id,
      linkProps: profile?.company_id
        ? {
            url: EntityRoutes.CompanyProfile
          }
        : ''
    },
    ...buildEmailLabels({ profile, entityType: EntityType.HiringAuthority }),
    {
      title: 'Linkedin Profile',
      mode: InfoLabelModes.EXTERNAL_LINK,
      linkProps: {
        url: profile?.link_profile ?? ''
      }
    }
  ];

  const phoneLabels = !isMultiplePhonesEnabled
    ? legacyPhoneLabels
    : nestTernary(
        hasMultiplePhones,
        getPhonesLabelsForProfile(profile, EntityType.HiringAuthority),
        legacyPhoneLabels
      );

  const alerts = getEmailValidationAlerts({
    profile,
    loading
  }).map(item => ({
    componentProps: item,
    Component: ProfileAlert
  }));

  const cards = [
    ...alerts,
    ...buildKeywordTagsCard({
      Component: KeywordTags,
      keywordTags: profile?.keywordTags,
      isEnabled: shouldShowKeywordTags
    }),
    {
      cardProps: { headerProps: { title: 'General Information' } },
      Component: InfoLabelsGrid,
      componentProps: { labels: [...generalLabels, ...phoneLabels] },
      className: 'generalCard'
    }
  ];

  return <InventoryOverviewLayout loading={loading} items={cards} />;
};

HiringAuthorityOverview.defaultProps = {
  profile: {}
};

export default HiringAuthorityOverview;
